import { useState } from 'react';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

export const useBulkImport = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '') continue;

      const row = {};
      const values = parseCSVLine(lines[i]);
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      rows.push(row);
    }

    return rows;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const importLessons = async (courseId, csvFile) => {
    setLoading(true);
    setProgress({ current: 0, total: 0, currentItem: '' });
    setResults(null);

    try {
      // Read CSV file
      const csvText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(csvFile);
      });

      // Parse CSV
      const rows = parseCSV(csvText);
      setProgress({ current: 0, total: rows.length, currentItem: 'Parsing CSV...' });

      const errors = [];
      let successful = 0;
      let failed = 0;

      // Group lessons by module
      const moduleGroups = {};
      rows.forEach((row, index) => {
        if (!moduleGroups[row.module]) {
          moduleGroups[row.module] = [];
        }
        moduleGroups[row.module].push({ ...row, rowIndex: index + 2 });
      });

      let currentRow = 0;

      // Process each module
      for (const [moduleName, lessons] of Object.entries(moduleGroups)) {
        try {
          setProgress({ 
            current: currentRow, 
            total: rows.length, 
            currentItem: `Creating module: ${moduleName}` 
          });

          // Create or get module
          let module = await findOrCreateModule(courseId, moduleName);
          
          // Process lessons in this module
          for (const lessonRow of lessons) {
            currentRow++;
            setProgress({ 
              current: currentRow, 
              total: rows.length, 
              currentItem: `Creating lesson: ${lessonRow.lessonTitle}` 
            });

            try {
              await createLessonFromRow(module.id, lessonRow, lessons.indexOf(lessonRow));
              successful++;
            } catch (error) {
              failed++;
              errors.push({
                row: lessonRow.rowIndex,
                message: error.message
              });
            }
          }
        } catch (error) {
          // Module creation failed - mark all lessons in this module as failed
          lessons.forEach(lessonRow => {
            failed++;
            errors.push({
              row: lessonRow.rowIndex,
              message: `Module creation failed: ${error.message}`
            });
          });
          currentRow += lessons.length;
        }
      }

      setResults({
        total: rows.length,
        successful,
        failed,
        errors
      });

      if (successful > 0) {
        toast.success(`Successfully imported ${successful} lessons`);
      }
      if (failed > 0) {
        toast.error(`${failed} lessons failed to import`);
      }

    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to process CSV file');
      setResults({
        total: 0,
        successful: 0,
        failed: 1,
        errors: [{ row: 1, message: error.message }]
      });
    } finally {
      setLoading(false);
    }
  };

  const findOrCreateModule = async (courseId, moduleName) => {
    // First, try to find existing module
    const { data: existingModule } = await supabase
      .from('modules')
      .select('*')
      .eq('course_id', courseId)
      .eq('title', moduleName)
      .single();

    if (existingModule) {
      return existingModule;
    }

    // Get the next order index
    const { data: modules } = await supabase
      .from('modules')
      .select('order_index')
      .eq('course_id', courseId)
      .order('order_index', { ascending: false })
      .limit(1);

    const nextOrderIndex = modules && modules.length > 0 ? modules[0].order_index + 1 : 0;

    // Create new module
    const { data: newModule, error } = await supabase
      .from('modules')
      .insert([{
        course_id: courseId,
        title: moduleName,
        order_index: nextOrderIndex
      }])
      .select()
      .single();

    if (error) throw error;
    return newModule;
  };

  const createLessonFromRow = async (moduleId, row, orderIndex) => {
    // Parse resources JSON
    let resources = [];
    if (row.resources && row.resources.trim()) {
      try {
        resources = JSON.parse(row.resources);
      } catch (e) {
        // If JSON parsing fails, ignore resources
        resources = [];
      }
    }

    // Validate required fields
    if (!row.lessonTitle || !row.lessonTitle.trim()) {
      throw new Error('Lesson title is required');
    }

    const lessonData = {
      module_id: moduleId,
      title: row.lessonTitle.trim(),
      video_embed_url: row.videoUrl ? row.videoUrl.trim() : null,
      text_content: row.textContent ? row.textContent.trim() : null,
      estimated_minutes: row.estimatedMinutes ? parseInt(row.estimatedMinutes) : 0,
      resources: resources,
      order_index: orderIndex
    };

    const { data, error } = await supabase
      .from('lessons')
      .insert([lessonData])
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  return {
    importLessons,
    loading,
    progress,
    results
  };
};